import React, { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'

import { Box, Select, Grid, Button, TextField } from '@the-platform-group/component-library'
import LocationTypeahead from '@the-platform-group/component-library/LocationTypeahead'

import formatLocationForSearch from '../formatLocationForSearch'
import { hasPrimarySearchField } from '../hooks/useRocketReachSearch'

const SELECT_BY_OPTIONS = {
  name: {
    key: 'name',
    label: 'Name',
  },
  email: {
    key: 'email',
    label: 'Email',
  },
  linkedin_url: {
    key: 'linkedin_url',
    label: 'LinkedIn URL',
  },
  employer: {
    key: 'employer',
    label: 'Company',
  },
  location_industry: {
    key: 'location_industry',
    label: 'Location & Industry',
  },
}

const JOB_CHANGE_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: 'one_week', label: 'Last 1 Week' },
  { value: 'one_month', label: 'Last 1 Month' },
  { value: 'three_months', label: 'Last 3 Months' },
]

const getSearchTermLabel = searchBy => {
  if (searchBy === 'employer') {
    return 'Company'
  }
  return `Contact ${
    searchBy.split('_').join(' ') ||
    'name, email, linkedin url, company & job change, or location & industry'
  }`
}

const RocketReachSearchFilters = ({ handleSearch }) => {
  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      searchBy: '',
      searchTerm: '',
      searchKeywords: '',
      location: '',
      industry: '',
      jobChangeSignal: '',
    },
  })

  const searchBy = watch('searchBy')
  const searchTerm = watch('searchTerm')
  const location = watch('location')
  const industry = watch('industry')

  const isCompanyMode = searchBy === 'employer'
  const isLocationIndustryMode = searchBy === 'location_industry'

  const canSearch = hasPrimarySearchField({ searchBy, searchTerm, location, industry })

  // Each mode has its own set of fields; a value left over from the
  // previous mode would either search on the wrong thing or block the
  // button by looking like valid input for the new mode.
  useEffect(() => {
    setValue('searchTerm', '')
    setValue('location', '')
    setValue('industry', '')
    setValue('jobChangeSignal', '')
    setValue('searchKeywords', '')
  }, [searchBy, setValue])

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleSubmit(handleSearch)()
    }
  }

  return (
    <Box component="form" sx={{ width: '100%', p: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6}>
          <Box
            component="form"
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              outline: '1px solid',
              outlineColor: 'grey.400',
              outlineOffset: '-1px',
              borderRadius: 1,
              '&:hover': {
                outlineColor: 'secondary.dark',
              },
              '&:focus-within': {
                outlineColor: 'primary.main',
                outlineWidth: '2px',
              },
            }}
          >
            <Controller
              name="searchBy"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  variant="outlined"
                  label="Search by"
                  placeholder="Select"
                  margin="none"
                  options={Object.values(SELECT_BY_OPTIONS).map(option => ({
                    value: option.key,
                    label: option.label,
                  }))}
                  sx={{
                    minWidth: '210px',
                    '& fieldset': {
                      border: 'unset',
                      borderRight: '1px solid',
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                      borderColor: 'grey.400',
                    },
                    '& .MuiFormLabel-root': {
                      backgroundColor: 'background.default',
                      px: 1,
                    },
                    // Apply hover styles when parent Box is hovered
                    '.MuiBox-root:hover &': {
                      '& fieldset': {
                        borderColor: 'secondary.dark',
                      },
                    },
                    // Apply focus styles when parent Box has focus-within
                    '.MuiBox-root:focus-within &': {
                      '& fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: '2px',
                      },
                    },
                  }}
                />
              )}
            />
            {isLocationIndustryMode ? (
              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, ...field } }) => (
                  <LocationTypeahead
                    {...field}
                    label="Location"
                    // Defaults to type="address", which admits street-level
                    // results alongside localities. This field only wants
                    // places, and "city" is the only value AWS filters
                    // server-side (IncludePlaceTypes: ["Locality"]).
                    type="city"
                    // LocationTypeahead wires its own onChange onto the
                    // underlying Autocomplete and then spreads any extra
                    // props after it, so a raw react-hook-form `onChange`
                    // (from spreading `field` whole) would silently win over
                    // onAddressSelect and the picked value would never reach
                    // the form. Keep onChange out of the spread and drive
                    // selection only through the typeahead's own callbacks.
                    onAddressChange={onChange}
                    onAddressSelect={address => onChange(formatLocationForSearch(address))}
                    sx={{
                      width: '100%',
                      '& fieldset': {
                        border: 'unset',
                      },
                      '& .MuiFormLabel-root:not(.Mui-disabled)': {
                        backgroundColor: 'background.default',
                        px: 1,
                      },
                    }}
                  />
                )}
              />
            ) : (
              <Controller
                name="searchTerm"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="contactNameOrEmail"
                    disabled={!searchBy}
                    label={getSearchTermLabel(searchBy)}
                    variant="outlined"
                    margin="none"
                    onKeyDown={handleKeyDown}
                    sx={{
                      width: '100%',
                      '& fieldset': {
                        border: 'unset',
                      },
                      '& .MuiFormLabel-root:not(.Mui-disabled)': {
                        backgroundColor: 'background.default',
                        px: 1,
                      },
                    }}
                  />
                )}
              />
            )}
          </Box>
        </Grid>
        {isCompanyMode && (
          <Grid item xs={2}>
            <Controller
              name="jobChangeSignal"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Job change"
                  options={JOB_CHANGE_OPTIONS}
                  sx={{ width: '100%' }}
                  // "Any time" (value: '') is a real selection, but MUI's Select
                  // treats an empty value as unfilled and hides it unless told
                  // otherwise, so the matching label must be forced to display.
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: selected =>
                      JOB_CHANGE_OPTIONS.find(option => option.value === selected)?.label,
                  }}
                  // MUI floats the label based on whether the value looks
                  // "filled", independently of displayEmpty above, so without
                  // this the label stays centered and overlaps the rendered
                  // value text instead of floating above it.
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
        )}
        {isLocationIndustryMode && (
          <Grid item xs={2}>
            <Controller
              name="industry"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Industry"
                  placeholder="Industry"
                  sx={{ width: '100%' }}
                />
              )}
            />
          </Grid>
        )}
        <Grid item xs={isCompanyMode || isLocationIndustryMode ? 3 : 4}>
          <Controller
            name="searchKeywords"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={!canSearch}
                label="Keywords"
                placeholder="Keywords"
                variant="outlined"
                sx={{ width: '100%' }}
              />
            )}
          />
        </Grid>
        <Grid item xs={isCompanyMode || isLocationIndustryMode ? 1 : 2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(handleSearch)}
            disabled={!canSearch}
            type="submit"
          >
            Search
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}

export default RocketReachSearchFilters
